import { CircularProgress } from '@mui/material';
import cn from 'classnames';
import { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CircleProgramme,
  ProgrammeCheckinAnswer,
  ProgrammeCheckins,
  ProgrammeTemplateListItem,
  ProgrammeWeek,
  isNotFound,
  programmesApi,
} from 'src/api/programmes';
import { MuiModal } from 'src/components/material/MuiModal';

import { useTabActive } from '../Tabs/tabActiveContext';
import styles from './Programme.module.scss';
import {
  ANSWER_LABEL,
  ANSWER_ORDER,
  CHECKINS_EMPTY_TEXT,
  CHECKINS_TEXT,
  CHECKINS_TITLE,
  CHECKIN_CHANGE_LABEL,
  CHECKIN_FAILED_TEXT,
  CHECKIN_NOTE_LABEL,
  CHECKIN_NOTE_MAX_LENGTH,
  CHECKIN_NOTE_PLACEHOLDER,
  CHECKIN_ONLY_LEADER_SEES,
  CHECKIN_QUESTION,
  CHECKIN_SAVED_TEXT,
  CHECKIN_SAVE_LABEL,
  CHECKIN_SAVING_LABEL,
  COMPLETED_KICKER,
  COMPLETED_TEXT,
  COMPLETED_TOAST_TEXT,
  COMPLETE_CONFIRM_TEXT,
  COMPLETE_FAILED_TEXT,
  COMPLETE_LABEL,
  COPIED_TEXT,
  COPY_CODE_LABEL,
  COPY_FAILED_TEXT,
  COPY_LINK_LABEL,
  COUNTS_TITLE,
  COUNT_LEGEND,
  EARLIER_WEEKS_TITLE,
  GUIDE_HIDE_LABEL,
  GUIDE_SHOW_LABEL,
  GUIDE_TITLE,
  JOIN_PAGE_HINT,
  LATER_WEEKS_TEXT,
  LEADER_MEASURE_HINT,
  LEADER_MOVE_FAILED_TEXT,
  LEADER_WEEK_NEXT_LABEL,
  LEADER_WEEK_PREVIOUS_LABEL,
  LEADER_WEEK_TEXT,
  LOADING_TEXT,
  MEASURE_HINT,
  MEETING_TITLE,
  NOT_ANSWERED_LABEL,
  NOT_STARTED_LEADER_TEXT,
  NOT_STARTED_LEADER_TITLE,
  NOT_STARTED_MEMBER_TEXT,
  NOT_STARTED_MEMBER_TITLE,
  REGISTRATION_CODE_LABEL,
  REGISTRATION_LINK_LABEL,
  REGISTRATION_NO_CODE_TEXT,
  REGISTRATION_TEXT,
  REGISTRATION_TITLE,
  START_FAILED_TEXT,
  START_LABEL,
  STARTED_TEXT,
  STARTING_LABEL,
  STEP_TITLE,
  UNAVAILABLE_TEXT,
  UNKNOWN_NAME,
  answeredText,
  checkinFor,
  checkinsCountText,
  currentWeek,
  earlierWeeks,
  guideBlocks,
  movedText,
  registrationLink,
  templateSummary,
  weekLabel,
} from './programmeText';

interface ProgrammeTabProps {
  circleId: string;
  isLeader: boolean;
  /** The Circle's six-character join code, shown to leaders; absent until made. */
  joinCode?: string | null;
}

type ProgrammeState =
  | { kind: 'loading' }
  | { kind: 'notStarted' }
  | { kind: 'ready'; programme: CircleProgramme }
  | { kind: 'error' };

/**
 * The "Program" tab of a LONELINESS Circle: this week's theme and step with
 * the member's check-in, or the leader's week controls, check-in overview
 * and guide. Fetched each time the tab is opened.
 */
export const ProgrammeTab: FC<ProgrammeTabProps> = ({
  circleId,
  isLeader,
  joinCode = null,
}) =>
  isLeader ? (
    <LeaderView circleId={circleId} joinCode={joinCode} />
  ) : (
    <MemberView circleId={circleId} />
  );

const Loading: FC = () => (
  <div className={styles.loading} role="status">
    <CircularProgress size={28} />
    <span>{LOADING_TEXT}</span>
  </div>
);

const useProgramme = (
  circleId: string
): [ProgrammeState, (state: ProgrammeState) => void] => {
  const isActive = useTabActive();
  const [state, setState] = useState<ProgrammeState>({ kind: 'loading' });

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }
    let cancelled = false;
    programmesApi
      .get(circleId)
      .then((programme) => {
        if (!cancelled) {
          setState({ kind: 'ready', programme });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ kind: isNotFound(error) ? 'notStarted' : 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, circleId]);

  return [state, setState];
};

const WeekCards: FC<{
  week: ProgrammeWeek;
  total: number;
  completed: boolean;
  isLeader: boolean;
  children?: React.ReactNode;
}> = ({ week, total, completed, isLeader, children }) => {
  const hint = week.assessment_window
    ? (isLeader ? LEADER_MEASURE_HINT : MEASURE_HINT)[week.assessment_window]
    : null;
  return (
    <>
      <div className={styles.header}>
        <div className={styles.kicker}>
          {completed ? COMPLETED_KICKER : weekLabel(week.number, total)}
        </div>
        <h2 className={styles.title}>{week.title}</h2>
      </div>

      {completed ? <div className={styles.done}>{COMPLETED_TEXT}</div> : null}

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>{MEETING_TITLE}</h3>
        <p className={styles.cardText}>{week.meeting_text}</p>
      </div>

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>{STEP_TITLE}</h3>
        <p className={styles.stepText}>{week.step_text}</p>
        {hint ? <div className={styles.hint}>{hint}</div> : null}
        {children}
      </div>
    </>
  );
};

/* Member */

const MemberView: FC<{ circleId: string }> = ({ circleId }) => {
  const [state, setState] = useProgramme(circleId);
  const [editing, setEditing] = useState(false);
  const [answer, setAnswer] = useState<ProgrammeCheckinAnswer | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  if (state.kind === 'loading') {
    return <Loading />;
  }
  if (state.kind === 'error') {
    return <p className={styles.text}>{UNAVAILABLE_TEXT}</p>;
  }
  if (state.kind === 'notStarted') {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{NOT_STARTED_MEMBER_TITLE}</h2>
          <p className={styles.text}>{NOT_STARTED_MEMBER_TEXT}</p>
        </div>
      </div>
    );
  }

  const { programme } = state;
  const week = currentWeek(programme);
  if (!week) {
    return <p className={styles.text}>{UNAVAILABLE_TEXT}</p>;
  }
  const completed = programme.status === 'COMPLETED';
  const own = checkinFor(programme.my_checkins, week.number);
  const showForm = programme.can_check_in && (editing || !own);

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!answer) {
      return;
    }
    setSaving(true);
    try {
      const saved = await programmesApi.checkIn(circleId, {
        week: week.number,
        answer,
        note: note.trim() || undefined,
      });
      toast.success(CHECKIN_SAVED_TEXT);
      setEditing(false);
      setState({
        kind: 'ready',
        programme: {
          ...programme,
          my_checkins: [
            ...programme.my_checkins.filter(
              (checkin) => checkin.week !== saved.week
            ),
            saved,
          ],
        },
      });
    } catch {
      toast.error(CHECKIN_FAILED_TEXT);
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setAnswer(own?.answer ?? null);
    setNote(own?.note ?? '');
    setEditing(true);
  };

  const earlier = earlierWeeks(programme);
  const remaining = programme.weeks_total - week.number;

  return (
    <div className={styles.container}>
      <WeekCards
        week={week}
        total={programme.weeks_total}
        completed={completed}
        isLeader={false}
      >
        {programme.can_check_in || own ? (
          <div className={styles.checkin}>
            {showForm ? (
              <form onSubmit={handleSave}>
                <p className={styles.question}>{CHECKIN_QUESTION}</p>
                <div
                  className={styles.answers}
                  role="radiogroup"
                  aria-label={CHECKIN_QUESTION}
                >
                  {ANSWER_ORDER.map((value) => (
                    <label
                      key={value}
                      className={cn(styles.pill, {
                        [styles.pillSelected]: answer === value,
                      })}
                    >
                      <input
                        type="radio"
                        name={`checkin-${week.number}`}
                        value={value}
                        checked={answer === value}
                        onChange={() => setAnswer(value)}
                        className={styles.hiddenInput}
                      />
                      {ANSWER_LABEL[value]}
                    </label>
                  ))}
                </div>
                <label className={styles.noteLabel}>
                  {CHECKIN_NOTE_LABEL}
                  <textarea
                    className={styles.noteInput}
                    value={note}
                    maxLength={CHECKIN_NOTE_MAX_LENGTH}
                    placeholder={CHECKIN_NOTE_PLACEHOLDER}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </label>
                <div className={styles.actions}>
                  <button
                    type="submit"
                    className={styles.primaryButton}
                    disabled={!answer || saving}
                  >
                    {saving ? CHECKIN_SAVING_LABEL : CHECKIN_SAVE_LABEL}
                  </button>
                  <span className={styles.quiet}>
                    {CHECKIN_ONLY_LEADER_SEES}
                  </span>
                </div>
              </form>
            ) : own ? (
              <>
                <div className={styles.done}>
                  {answeredText(own.answer)}
                  {own.note ? ` ${own.note}` : ''}
                </div>
                {programme.can_check_in ? (
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={startEditing}
                  >
                    {CHECKIN_CHANGE_LABEL}
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
      </WeekCards>

      {earlier.length > 0 ? (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>{EARLIER_WEEKS_TITLE}</h3>
          <div className={styles.weeks}>
            {earlier.map((past) => {
              const pastOwn = checkinFor(programme.my_checkins, past.number);
              return (
                <div key={past.number} className={styles.weekRow}>
                  <span className={styles.weekNumber}>Uke {past.number}</span>
                  <span>{past.step_text}</span>
                  <span
                    className={cn(styles.weekAnswer, {
                      [styles.weekAnswerMissing]: !pastOwn,
                    })}
                  >
                    {pastOwn
                      ? ANSWER_LABEL[pastOwn.answer]
                      : NOT_ANSWERED_LABEL}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {!completed && remaining > 0 ? (
        <p className={styles.quiet}>{LATER_WEEKS_TEXT(remaining)}</p>
      ) : null}
    </div>
  );
};

/* Registration card (leaders): the link and the code to hand out */

const RegistrationCard: FC<{ circleId: string; joinCode: string | null }> = ({
  circleId,
  joinCode,
}) => {
  const origin = window.location.origin;
  const link = registrationLink(origin, circleId);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(COPIED_TEXT);
    } catch {
      toast.error(COPY_FAILED_TEXT);
    }
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{REGISTRATION_TITLE}</h3>
      <p className={styles.text}>{REGISTRATION_TEXT}</p>
      <div className={styles.registrationRow}>
        <span className={styles.registrationLabel}>
          {REGISTRATION_LINK_LABEL}
        </span>
        <code className={styles.registrationValue}>{link}</code>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={() => copy(link)}
        >
          {COPY_LINK_LABEL}
        </button>
      </div>
      <div className={styles.registrationRow}>
        <span className={styles.registrationLabel}>
          {REGISTRATION_CODE_LABEL}
        </span>
        {joinCode ? (
          <>
            <code className={styles.joinCode}>{joinCode}</code>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => copy(joinCode)}
            >
              {COPY_CODE_LABEL}
            </button>
          </>
        ) : (
          <span className={styles.quiet}>{REGISTRATION_NO_CODE_TEXT}</span>
        )}
      </div>
      {joinCode ? (
        <p className={styles.quiet}>{JOIN_PAGE_HINT(origin)}</p>
      ) : null}
    </div>
  );
};

/* Leader */

const LeaderView: FC<{ circleId: string; joinCode: string | null }> = ({
  circleId,
  joinCode,
}) => {
  const isActive = useTabActive();
  const [state, setState] = useProgramme(circleId);
  const [templates, setTemplates] = useState<ProgrammeTemplateListItem[]>([]);
  const [checkins, setCheckins] = useState<ProgrammeCheckins | null>(null);
  const [starting, setStarting] = useState(false);
  const [moving, setMoving] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);

  const loadCheckins = useCallback(() => {
    let cancelled = false;
    programmesApi
      .getCheckins(circleId)
      .then((result) => {
        if (!cancelled) {
          setCheckins(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCheckins(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [circleId]);

  useEffect(() => {
    if (!isActive || state.kind !== 'ready') {
      return undefined;
    }
    return loadCheckins();
  }, [isActive, state.kind, loadCheckins]);

  useEffect(() => {
    if (!isActive || state.kind !== 'notStarted') {
      return undefined;
    }
    let cancelled = false;
    programmesApi
      .listTemplates('LONELINESS')
      .then((list) => {
        if (!cancelled) {
          setTemplates(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, state.kind, circleId]);

  if (state.kind === 'loading') {
    return <Loading />;
  }
  if (state.kind === 'error') {
    return <p className={styles.text}>{UNAVAILABLE_TEXT}</p>;
  }

  if (state.kind === 'notStarted') {
    const template = templates[0];
    const handleStart = async () => {
      setStarting(true);
      try {
        const programme = await programmesApi.start(
          circleId,
          template
            ? { template_key: template.key, template_version: template.version }
            : {}
        );
        toast.success(STARTED_TEXT);
        setState({ kind: 'ready', programme });
      } catch {
        toast.error(START_FAILED_TEXT);
      } finally {
        setStarting(false);
      }
    };
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{NOT_STARTED_LEADER_TITLE}</h2>
          <p className={styles.text}>{NOT_STARTED_LEADER_TEXT}</p>
        </div>
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>
            {template ? template.title : 'Program'}
          </h3>
          {template ? (
            <>
              <p className={styles.cardText}>{template.description}</p>
              <p className={styles.quiet}>
                {templateSummary(template.weeks_total)}
              </p>
            </>
          ) : null}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={starting}
              onClick={handleStart}
            >
              {starting ? STARTING_LABEL : START_LABEL}
            </button>
          </div>
        </div>
        <RegistrationCard circleId={circleId} joinCode={joinCode} />
      </div>
    );
  }

  const { programme } = state;
  const week = currentWeek(programme);
  if (!week) {
    return <p className={styles.text}>{UNAVAILABLE_TEXT}</p>;
  }
  const completed = programme.status === 'COMPLETED';

  const move = async (target: number) => {
    setMoving(true);
    try {
      const updated = await programmesApi.update(circleId, {
        current_week: target,
      });
      toast.success(movedText(updated.current_week));
      setState({ kind: 'ready', programme: updated });
    } catch {
      toast.error(LEADER_MOVE_FAILED_TEXT);
    } finally {
      setMoving(false);
    }
  };

  const complete = async () => {
    setConfirmComplete(false);
    setMoving(true);
    try {
      const updated = await programmesApi.update(circleId, {
        status: 'COMPLETED',
      });
      toast.success(COMPLETED_TOAST_TEXT);
      setState({ kind: 'ready', programme: updated });
    } catch {
      toast.error(COMPLETE_FAILED_TEXT);
    } finally {
      setMoving(false);
    }
  };

  const currentSummary = checkins?.weeks.find(
    (entry) => entry.week === week.number
  );

  return (
    <div className={styles.container}>
      <WeekCards
        week={week}
        total={programme.weeks_total}
        completed={completed}
        isLeader
      />

      {!completed ? (
        <div className={styles.card}>
          <p className={styles.text}>{LEADER_WEEK_TEXT}</p>
          <div className={styles.weekControls}>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={moving || week.number <= 1}
              onClick={() => move(week.number - 1)}
            >
              {LEADER_WEEK_PREVIOUS_LABEL}
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={moving || week.number >= programme.weeks_total}
              onClick={() => move(week.number + 1)}
            >
              {LEADER_WEEK_NEXT_LABEL}
            </button>
            {week.number >= programme.weeks_total ? (
              <button
                type="button"
                className={styles.dangerButton}
                disabled={moving}
                onClick={() => setConfirmComplete(true)}
              >
                {COMPLETE_LABEL}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <RegistrationCard circleId={circleId} joinCode={joinCode} />

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>{CHECKINS_TITLE}</h3>
        <p className={styles.text}>{CHECKINS_TEXT}</p>
        {checkins ? (
          checkins.members.length === 0 ? (
            <p className={styles.quiet}>{CHECKINS_EMPTY_TEXT}</p>
          ) : (
            <>
              {currentSummary ? (
                <p className={styles.quiet}>
                  {weekLabel(week.number, programme.weeks_total)}:{' '}
                  {checkinsCountText(
                    currentSummary.answered,
                    currentSummary.members
                  )}
                </p>
              ) : null}
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Deltaker</th>
                    <th>Uke {week.number}</th>
                  </tr>
                </thead>
                <tbody>
                  {checkins.members.map((member) => {
                    const entry = member.checkins[String(week.number)];
                    return (
                      <tr key={member.user_id}>
                        <td>{member.full_name ?? UNKNOWN_NAME}</td>
                        <td>
                          {entry ? (
                            <>
                              <span className={styles.weekAnswer}>
                                {ANSWER_LABEL[entry.answer]}
                              </span>
                              {entry.note ? (
                                <span className={styles.tableNote}>
                                  {entry.note}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className={styles.weekAnswerMissing}>
                              {NOT_ANSWERED_LABEL}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <h3 className={styles.cardTitle}>{COUNTS_TITLE}</h3>
              <div className={styles.counts}>
                {checkins.weeks
                  .filter((entry) => entry.week <= week.number)
                  .map((entry) => (
                    <div
                      key={entry.week}
                      className={cn(styles.countCard, {
                        [styles.countCardCurrent]: entry.week === week.number,
                      })}
                    >
                      <div className={styles.countWeek}>Uke {entry.week}</div>
                      <div className={styles.countValue}>
                        {entry.yes} / {entry.partly} / {entry.no}
                      </div>
                      <div className={styles.countLegend}>
                        {COUNT_LEGEND} ·{' '}
                        {checkinsCountText(entry.answered, entry.members)}
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )
        ) : (
          <p className={styles.quiet}>{LOADING_TEXT}</p>
        )}
      </div>

      {programme.leader_guide ? (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>{GUIDE_TITLE}</h3>
          <button
            type="button"
            className={styles.linkButton}
            aria-expanded={showGuide}
            onClick={() => setShowGuide((value) => !value)}
          >
            {showGuide ? GUIDE_HIDE_LABEL : GUIDE_SHOW_LABEL}
          </button>
          {showGuide ? (
            <div className={styles.guide}>
              {guideBlocks(programme.leader_guide).map((block, index) =>
                block.kind === 'heading' ? (
                  <h4 key={index} className={styles.guideHeading}>
                    {block.text}
                  </h4>
                ) : (
                  <p key={index} className={styles.guideParagraph}>
                    {block.text}
                  </p>
                )
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <MuiModal open={confirmComplete} setOpen={setConfirmComplete} width={440}>
        <div className={styles.modal}>
          <p className={styles.cardText}>{COMPLETE_CONFIRM_TEXT}</p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setConfirmComplete(false)}
            >
              Avbryt
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={complete}
            >
              {COMPLETE_LABEL}
            </button>
          </div>
        </div>
      </MuiModal>
    </div>
  );
};
